import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

/* ── helpers ───────────────────────────────────────── */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));
const isIntegerOrProperFraction = (value) => Number.isInteger(value) || (Math.abs(value) > 0 && Math.abs(value) < 1);

// „log” z ukrytą podstawą 10 (treść, inputy, odpowiedzi)
const logb = (a) => (a === 10 ? '\\log' : `\\log_{${a}}`);
// jawne wypisanie podstawy w wyjaśnieniach (od kroku 2)
const logExp = (a) => (a === 10 ? '\\log_{10}' : `\\log_{${a}}`);

// NWD + uproszczenia ułamków
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};
function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

// aproksymacja liczby ułamkiem (ułamki łańcuchowe)
function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };
  let h0 = 0,
    k0 = 1,
    h1 = 1,
    k1 = 0,
    y = x;
  for (let i = 0; i < 20; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0,
      k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1;
    k0 = k1;
    h1 = h2;
    k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }
  let num = h1,
    den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0;
    den = k0;
  }
  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

// ładny LaTeX (całkowita / zwykła / mieszana)
function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);
  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);
  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));
  const sgn = num < 0 ? '-' : '';
  num = Math.abs(num);
  return `${sgn}\\tfrac{${num}}{${den}}`;
}

function isPowerOfBase(c, a) {
  for (let k = -6; k <= 6; k++) if (Math.abs(c - Math.pow(a, k)) < 1e-10) return true;
  return false;
}

// „ładne” liczby do mnożenia/dzielenia
const NICE_BY_BASE = {
  10: [2, 4, 5, 8, 25, 50, 1 / 2, 1 / 4, 4 / 5, 20, 1 / 5],
  5: [2, 4, 8, 10, 1 / 2, 1 / 4],
  4: [2, 8, 1 / 2, 10, 1 / 4, 2 / 5, 5],
  3: [2, 6, 1 / 2, 3 / 4, 12, 1 / 4],
  2: [3, 6, 3 / 4, 5, 10, 1 / 5, 2 / 5],
};

// ── budowa trójki (X,Y,Z) ────────────────────────────
// Wariant "+−":  X*Y/Z = a^t  → bierzemy: X=R*a^t, Y=S, Z=R*S
// Wariant "−+":  X*Z/Y = a^t  → bierzemy: X=R*a^t, Z=S, Y=R*S
function buildTriple(a, t, variant) {
  const pool = (NICE_BY_BASE[a] || NICE_BY_BASE[10]).filter(
    (c) => c !== 1 && !isPowerOfBase(c, a)
  );
  for (let tries = 0; tries < 60; tries++) {
    const R = pick(pool),
      S = pick(pool);
    let X, Y, Z;
    if (variant === '+-') {
      X = R * powi(a, t);
      Y = S;
      Z = R * S;
    } else {
      // '-+'
      X = R * powi(a, t);
      Z = S;
      Y = R * S;
    }
    if (isFinite(X) && isFinite(Y) && isFinite(Z) && [X, Y, Z].every(isIntegerOrProperFraction)) {
      return {
        X,
        Y,
        Z,
        Xs: niceLatexFromNumber(X),
        Ys: niceLatexFromNumber(Y),
        Zs: niceLatexFromNumber(Z),
      };
    }
  }
  // awaryjnie
  const R = a === 2 ? 3 : 2,
    S = a === 3 ? 2 : 3;
  let X, Y, Z;
  if (variant === '+-') {
    X = R * powi(a, t);
    Y = S;
    Z = R * S;
  } else {
    X = R * powi(a, t);
    Z = S;
    Y = R * S;
  }
  return {
    X,
    Y,
    Z,
    Xs: niceLatexFromNumber(X),
    Ys: niceLatexFromNumber(Y),
    Zs: niceLatexFromNumber(Z),
  };
}

/* ── generator ─────────────────────────────────────── */
export function generateTask() {
  const a = pick([2, 3, 4, 5, 10]);
  const t = pick([-3, -2, -1, 1, 2, 3, 4]);
  const variant = pick(['+-', '-+']); // kolejność działań
  const { X, Y, Z, Xs, Ys, Zs } = buildTriple(a, t, variant);
  return { type: 'LogPropMixTask', data: { a, t, variant, X, Y, Z, Xs, Ys, Zs } };
}

/* ── render ────────────────────────────────────────── */
export function renderTask({ data }) {
  const { a, Xs, Ys, Zs, variant } = data;
  const expr =
    variant === '+-'
      ? `${logb(a)} ${Xs} + ${logb(a)} ${Ys} - ${logb(a)} ${Zs}`
      : `${logb(a)} ${Xs} - ${logb(a)} ${Ys} + ${logb(a)} ${Zs}`;
  return (
    <div>
      Oblicz wartość wyrażenia:
      <div
        style={{
          marginTop: 14,
          marginBottom: 14,
          fontSize: '16px',
          lineHeight: 1.5,
        }}
      >
        <BlockMath math={expr} />
      </div>
    </div>
  );
}

/* ── input ─────────────────────────────────────────── */
export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => {
    // tylko to, co wpisuje uczeń – bez _labelLatex
    onChange && onChange(ans);
  };

  return (
    <div className="space-y-2">
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        label="" // Podgląd: sama odpowiedź
      />
    </div>
  );
}

/* ── checking ──────────────────────────────────────── */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const v = userAnswer.parsed;
  if (v == null) return false;
  return Math.abs(v - data.t) < 1e-4;
}

/* ── answers ───────────────────────────────────────── */
export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer || typeof userAnswer !== 'object' || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  const { t } = data;
  return <InlineMath math={String(t)} />;
}

/* ── explanation ───────────────────────────────────── */
export function renderExplanation({ data }) {
  const { a, Xs, Ys, Zs, t, X, Y, Z, variant } = data;

  // pośrednie argumenty
  const firstArg = variant === '+-' ? X * Y : X / Y;
  const firstLatex = variant === '+-' ? `${Xs}\\cdot${Ys}` : `\\tfrac{${Xs}}{${Ys}}`;
  const firstNice = niceLatexFromNumber(firstArg);

  const finalArg = variant === '+-' ? firstArg / Z : firstArg * Z;
  const finalNice = niceLatexFromNumber(finalArg);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Najpierw używamy wzoru dla dwóch składników:
      </p>
      <BlockMath math={'\\log_{a} x + \\log_{a} y = \\log_{a}(xy)'} />
      <BlockMath math={'\\log_{a} x - \\log_{a} y = \\log_{a}\\!\\left(\\tfrac{x}{y}\\right)'} />
      {a === 10 && (
        <p style={{ marginTop: 6 }}>
          <em>Uwaga:</em> zapis <InlineMath math={"\\log"} /> oznacza logarytm dziesiętny, czyli{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            <InlineMath math={"\\log x = \\log_{10} x"} />
          </span>
          .
        </p>
      )}

      <p>
        <strong>Krok 2:</strong> Podstawiamy i najpierw{' '}
        {variant === '+-' ? 'sumujemy' : 'odejmujemy'}:
      </p>
      {variant === '+-' ? (
        <BlockMath
          math={`${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} - ${logExp(a)} ${Zs} = ${logExp(
            a
          )}\\!\\left(${firstLatex}\\right) - ${logExp(a)} ${Zs} = ${logExp(
            a
          )} ${firstNice} - ${logExp(a)} ${Zs}`}
        />
      ) : (
        <BlockMath
          math={`${logExp(a)} ${Xs} - ${logExp(a)} ${Ys} + ${logExp(a)} ${Zs} = ${logExp(
            a
          )}\\!\\left(${firstLatex}\\right) + ${logExp(a)} ${Zs} = ${logExp(
            a
          )} ${firstNice} + ${logExp(a)} ${Zs}`}
        />
      )}

      <p>
        <strong>Krok 3:</strong> Następnie {variant === '+-' ? 'odejmujemy' : 'dodajemy'} i
        obliczamy:
      </p>
      {variant === '+-' ? (
        <BlockMath
          math={`${logExp(a)} ${firstNice} - ${logExp(a)} ${Zs} = ${logExp(
            a
          )}\\!\\left(\\tfrac{${firstNice}}{${Zs}}\\right) = ${logExp(
            a
          )} ${finalNice} = ${t}`}
        />
      ) : (
        <BlockMath
          math={`${logExp(a)} ${firstNice} + ${logExp(a)} ${Zs} = ${logExp(
            a
          )}\\!\\left(${firstNice}\\cdot${Zs}\\right) = ${logExp(a)} ${finalNice} = ${t}`}
        />
      )}

      <p>
        <strong>Wniosek:</strong>
      </p>
      {variant === '+-' ? (
        <BlockMath math={`${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} - ${logExp(a)} ${Zs} = ${t}`} />
      ) : (
        <BlockMath math={`${logExp(a)} ${Xs} - ${logExp(a)} ${Ys} + ${logExp(a)} ${Zs} = ${t}`} />
      )}
    </div>
  );
}
